import { Box, Grid, styled, Typography, Link, ListItem, List, useMediaQuery } from '@mui/material';
import CustomTitleWithBlueBackground from '../../../common/CustomTitleWithBlueBackground';
import {
  primaryBlueLinkStyle,
  primaryBulletStyle,
  primaryListItemStyle,
  secondaryListItemStyle,
} from '../../../../styles/HowToUnderstandPFASStyles';
import { useThemeContext } from '../../../../contexts/BaseThemeContextProvider';

const HowToReducePfas = () => {
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const renderImmediateIdeasToTry = () => {
    const immediateIdeas = [
      {
        title: 'Eat less fast food and processed foods',
        content: 'Many food wrappers and bags, as well as to-go containers and cups, contain PFAS.',
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas.eatLessIcon,
      },
      {
        title: "Don't use plastic containers",
        content: 'Use metal or glass containers for storing leftovers at home.',
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas.noPlasticIcon,
      },
      {
        title: 'Say NO to single-use cups',
        content: 'Use metal or glass mugs and cups for beverages, instead of single-use cups.',
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas.noSingleUseCup,
      },
      {
        title: 'Get your water supply tested',
        content: (
          <>
            Test your water supply for PFAS using a service such as{' '}
            <Link
              href="https://mytapscore.com/products/pfas-water-test?srsltid=AfmBOordof7wM9JsLSGxnlFkfXGQb9VUFfBPIVHrnjnkQdWI518jR_11"
              target="_blank"
              rel="noopener noreferrer"
              sx={primaryBlueLinkStyle}
            >
              Tap Score
            </Link>
            . If it is too high, install a water filter that reduces PFAS.
          </>
        ),
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas.getWaterTestIcon,
      },
      {
        title: 'Avoid non-stick pans',
        content:
          'Reduce the use of non-stick pans and cookware, especially if non-stick coatings are scratched or damaged. Stainless steel and cast iron are good alternatives.',
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas.avoidNonStickIcon,
      },
      {
        title: 'Avoid stain-resistant fabrics',
        content:
          'Fabrics and leather in furniture covers are often treated with PFAS. Reduce the use of fabrics and furniture that are treated to be “stain-resistant”.',
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas
          .avoidStrainResistantFabrics,
      },
      {
        title: 'Choose PFAS-free cosmetics',
        content: (
          <>
            You can find a list of these on this website:{' '}
            <Link
              href="https://pfascentral.org/pfas-free-products/"
              target="_blank"
              rel="noopener noreferrer"
              sx={primaryBlueLinkStyle}
            >
              https://pfascentral.org/pfas-free-products/
            </Link>
          </>
        ),
        icon: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.immediateIdeas.pfasFreeCosmeticsIcon,
      },
    ];

    const IdeaCard = styled(Box)({
      backgroundColor: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.ideaCard.backgroundColor,
      borderRadius: '16px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      gap: '12px',
      height: '100%',
    });

    const Icon = styled('img')({
      width: '40px',
      height: '40px',
      flexShrink: 0,
    });

    return (
      <Box sx={{ marginBottom: '24px', textAlign: 'left' }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '22px',
            fontWeight: 400,
            color: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.typography.body1.color,
          }}
        >
          Reducing PFAS Exposure
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.typography.body2.color,
            marginBottom: '32px',
          }}
        >
          We are constantly exposed to PFAS chemicals due to their widespread use, but making small, easy changes can
          add up to meaningful improvements and reduce your exposure to PFAS.
        </Typography>
        <CustomTitleWithBlueBackground
          text={
            isMobile
              ? 'Some immediate ideas to try in your daily\nliving are:'
              : 'Some immediate ideas to try in your daily living are:'
          }
          gutterBottom={false}
          variant="body2"
          sx={{ fontSize: '16px', fontWeight: 500, padding: '8px', textAlign: 'left' }}
        />

        <Grid container spacing={2} sx={{ marginTop: '16px' }}>
          {immediateIdeas.map((idea, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
                <Icon src={idea.icon} alt={`${idea.title} icon`} />
                <IdeaCard>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.ideaCard.color,
                        lineHeight: '16px',
                        marginBottom: '4px',
                      }}
                    >
                      {idea.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: theme.custom.pages.howTo.howToReducePfas.renderImmediateIdeasToTry.ideaCard.color,
                        lineHeight: '16px',
                      }}
                    >
                      {idea.content}
                    </Typography>
                  </Box>
                </IdeaCard>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderWorkplacePFASListItems = () => {
    return (
      <Box sx={{ pl: 2, marginBottom: '32px' }}>
        <List dense sx={{ listStyleType: 'disc' }}>
          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Firefighters{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , due to the use of PFAS in turnout gear and aqueous film-forming foams (AFFF).
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Military personnel and veterans{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , since PFAS is widely used in their gear and also many military sites are contaminated.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Textile or carpet workers{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , because of the use of PFAS in stain and water resistant coatings.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Construction workers{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , since a large number of materials contain PFAS.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Automotive workers{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , because PFAS is often added to lubricants, hydraulic fluid, and other materials or fluids used in
              vehicles.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Chemical workers{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , especially ones involved in the manufacture of PFAS and related products.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Healthcare workers{''}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              , due to the widespread use of PFAS in medical equipment, especially latex gloves and other disposables.
            </Typography>
          </ListItem>
        </List>
      </Box>
    );
  };

  const renderWorkplacePFASInfo = () => {
    return (
      <Box
        sx={{
          padding: '24px',
          borderRadius: '16px',
          backgroundColor:
            theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.renderWorkplacePFASInfo.boxStyles
              .backgroundColor,
          marginBottom: '32px',
        }}
      >
        <Typography
          sx={{
            textAlign: 'left',
            color: theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.renderWorkplacePFASInfo.color,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
          }}
        >
          {
            "If you are exposed to PFAS at work, it's important to understand where the risk of exposure is coming from. Using appropriate personal protective equipment (PPE) and following workplace safety guidelines are a first step to reducing exposure to PFAS. Proper ventilation or air filtration can also help reduce exposure through the air. If possible, work with your employer to reduce the use of PFAS-containing materials in the workplace."
          }
        </Typography>
      </Box>
    );
  };

  const renderWorkplacePFASExposure = () => {
    return (
      <Box
        sx={{
          textAlign: 'left',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '22px',
            fontWeight: 400,
            color: theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.typography.body1.color,
            lineHeight: '28px',
          }}
        >
          Workplace PFAS exposure
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.typography.body2.color,
            marginBottom: '32px',
            lineHeight: '20px',
          }}
        >
          Several occupations are known to have a higher risk of PFAS exposure. These are:
        </Typography>
        {renderWorkplacePFASListItems()}
        {renderWorkplacePFASInfo()}
      </Box>
    );
  };

  const renderImportanceOfPFASMonitoring = () => {
    return (
      <Box sx={{ textAlign: 'left' }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '22px',
            fontWeight: 400,
            color:
              theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.renderImportanceOfPFASMonitoring
                .typography.body1.color,
            lineHeight: '28px',
          }}
        >
          The importance of ongoing PFAS monitoring
        </Typography>
        <Typography
          variant="body2"
          sx={{
            marginTop: '32px',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color:
              theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.renderImportanceOfPFASMonitoring
                .typography.body2.color,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
            }}
          >
            PFAS are often inaccurately called “Forever Chemicals”
          </Typography>
          {'. '}
          This is because many of them take a long time to break down in the environment. They also can take a long time
          to be eliminated from the human body. The key measure here is called the elimination half-life. Elimination
          half life is a measure of how much time it takes for a compound to be removed from the body. PFAS compounds
          sometimes have half lives measured in years if not decades.
        </Typography>
        <Typography
          sx={{
            marginTop: '16px',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            color:
              theme.custom.pages.howTo.howToReducePfas.renderWorkplacePFASExposure.renderImportanceOfPFASMonitoring
                .typography.body2.color,
          }}
        >
          If a compound has a half life of 2 years that means after 2 years, half of it is still in the body. And after
          4 years a quarter is left. As a rule of thumb, scientists say that it takes five half-lives to effectively
          eliminate a chemical compound from the body. So, for many PFAS it takes decades for them to be eliminated.
          However, some modern PFAS compounds have far shorter half lives in the body. Therefore, by taking the steps
          above, you should see your blood levels dropping within a few months. The generally accepted advice is for
          most people to take a test once or twice per year. Those in high risk occupations or living in high
          contamination areas should consider taking tests 3 or 4 times a year.
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderBottomRightRadius: '16px',
          borderBottomLeftRadius: '16px',
          padding: { xs: '24px 16px', sm: '24px 40px 40px 40px' },
          backgroundColor: theme.custom.pages.howTo.howToReducePfas.backgroundColor,
        }}
      >
        {renderImmediateIdeasToTry()}
      </Box>
      <Box
        sx={{
          marginTop: '24px',
          width: '100%',
          padding: { xs: '24px 16px', sm: '24px 40px 40px 40px' },
          borderRadius: '16px',
          backgroundColor: theme.custom.pages.howTo.howToReducePfas.backgroundColor,
        }}
      >
        {renderWorkplacePFASExposure()}
        {renderImportanceOfPFASMonitoring()}
      </Box>
    </>
  );
};

export default HowToReducePfas;
